import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Select, Symbol } from '../../../ui'
import { useAutomationEditor } from '../editor-context'
import type { TriggerEventType, TriggerNodeData } from '../types'

export function TriggerNode({ id, data }: NodeProps) {
  const nodeData = data as unknown as TriggerNodeData
  const event = nodeData.event ?? 'tasks.completed'
  const {
    nodes,
    edges,
    connectingNodeId,
    startConnecting,
    cancelConnecting,
    connectNodes,
    disconnectEdge,
  } = useAutomationEditor()

  const isConnectingFromHere = connectingNodeId === id

  // Ausgehende Kanten von diesem Trigger ermitteln
  const outgoingEdges = edges.filter((e) => e.source === id)
  const connectedTargetIds = new Set(outgoingEdges.map((e) => e.target))

  // Alle anderen Nodes, die noch nicht mit diesem Trigger verbunden sind
  const availableTargets = nodes
    .filter((n) => n.id !== id && !connectedTargetIds.has(n.id) && n.type !== 'trigger')
    .map((n) => {
      const label = n.type === 'condition' ? 'UND (Bedingung)' : 'DANN (Aktion)'
      return { id: n.id, label }
    })

  const updateEvent = (newEvent: string) => {
    nodeData.event = newEvent as TriggerEventType
    window.dispatchEvent(new CustomEvent('node-data-change', { detail: { id } }))
  }

  const handleDirectConnect = (targetId: string) => {
    if (targetId) {
      connectNodes(id, targetId)
    }
  }

  return (
    <div
      className={`editor-node editor-node--trigger ${isConnectingFromHere ? 'editor-node--connecting-source' : ''}`}
      data-id={id}
    >
      <div className="editor-node__kopf">
        <span className="editor-node__symbol">
          <Symbol name="automation" width={18} height={18} />
        </span>
        <span className="editor-node__kategorie">WENN (Auslöser)</span>
      </div>

      <div className="editor-node__inhalt">
        <Select id={`trigger-sel-${id}`} label="Ereignis" wert={event} onWert={updateEvent}>
          <option value="tasks.completed">Aufgabe erledigt</option>
          <option value="meals.planned">Mahlzeit auf Wochenplan</option>
          <option value="allowance.booked">Taschengeld verbucht</option>
        </Select>

        {/* Verknüpfungs-Bereich direkt auf der Karte */}
        <div className="editor-node__verbindungen">
          <div className="editor-node__verbindungen-kopf">
            <span className="editor-node__verbindungen-titel">Verknüpft mit:</span>
            {!isConnectingFromHere ? (
              <button
                type="button"
                className="btn btn--tonal editor-node__connect-btn"
                onClick={() => startConnecting(id)}
                title="Klicken, um diesen Baustein mit einem anderen zu verbinden"
              >
                + Verbinden
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--text editor-node__connect-btn"
                onClick={cancelConnecting}
              >
                Abbrechen
              </button>
            )}
          </div>

          {outgoingEdges.length > 0 && (
            <div className="editor-node__chips">
              {outgoingEdges.map((e) => {
                const targetNode = nodes.find((n) => n.id === e.target)
                const targetLabel =
                  targetNode?.type === 'condition'
                    ? 'Bedingung'
                    : targetNode?.type === 'action'
                      ? 'Aktion'
                      : e.target
                return (
                  <span key={e.id} className="editor-chip">
                    <span>→ {targetLabel}</span>
                    <button
                      type="button"
                      className="editor-chip__loeschen"
                      onClick={() => disconnectEdge(e.id)}
                      title="Verbindung trennen"
                      aria-label="Verbindung trennen"
                    >
                      ✕
                    </button>
                  </span>
                )
              })}
            </div>
          )}

          {availableTargets.length > 0 && (
            <div className="editor-node__schnell-wahl">
              <Select
                id={`trigger-connect-${id}`}
                label="Verknüpfen"
                className="editor-node__mini-select"
                wert=""
                onWert={handleDirectConnect}
              >
                <option value="" disabled>
                  + Mit Baustein verknüpfen …
                </option>
                {availableTargets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>

      <Handle
        id="trigger-out"
        type="source"
        position={Position.Right}
        isConnectable={true}
        title="Verbindungspunkt: Ziehen oder anklicken, um mit nächstem Schritt zu verbinden"
        className="editor-handle editor-handle--source"
      />
    </div>
  )
}
